import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  Stack,
  LinearProgress
} from '@mui/material'
import {
  PhoneMissed as MissedIcon,
  Close as CloseIcon,
  PhoneInTalk as CallIcon,
  PersonAdd as AddLeadIcon,
  ArrowForward as ArrowIcon,
  Business as CompanyIcon,
  MonetizationOn as DealIcon,
  AssignmentInd as LeadIcon,
  Storefront as SalesIcon
} from '@mui/icons-material'
import { missedCallStore, type MissedCallAlert } from '../../lib/stores/missedCallStore'
import { maskPhoneNumber } from '../../utils/phoneUtils'
import { callStore } from '../../lib/stores/callStore'
import { LeadDrawer } from '../drawers/LeadDrawer'
import { leadsApi } from '../../lib/api/leadsApi'
import { type LeadFormData } from '../../lib/schemas/leadSchema'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

export const MissedCallContainer = observer(function MissedCallContainer() {
  const [createLeadPhone, setCreateLeadPhone] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const createLeadMutation = useMutation({
    mutationFn: (data: LeadFormData) =>
      leadsApi.create({
        name: `${data.firstName} ${data.lastName || ''}`.trim(),
        phone: data.contactNumber,
        email: data.email || undefined,
        source: data.source,
        companyName: data.companyName,
        address: data.address || undefined,
        gst: data.gst || undefined,
        pincode: data.pincode || undefined,
        orgId: data.orgId || undefined,
        lobId: data.lobId || undefined,
        warehouseId: data.warehouseId || undefined,
        nextAction: data.description || undefined,
        designation: data.designation || undefined,
        websiteUrl: data.companyWebsite || undefined,
        industryType: data.industry || undefined,
        customerTypeId: data.customerTypeId || undefined,
        fileType: data.fileType || undefined
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['leads-list'] })
      setCreateLeadPhone(null)
      toast.success('Lead created successfully!')
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to create lead')
    }
  })

  if (missedCallStore.alerts.length === 0 && !createLeadPhone) return null

  const phone10Digits = createLeadPhone
    ? createLeadPhone.replace(/\D/g, '').slice(-10)
    : ''

  return (
    <>
      {/* Floating Alerts Container */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          maxWidth: 420,
          width: '100%',
          pointerEvents: 'none'
        }}
      >
        {missedCallStore.alerts.map(alert => (
          <MissedCallCard
            key={alert.id}
            alert={alert}
            onCreateLead={phone => setCreateLeadPhone(phone)}
          />
        ))}
      </Box>

      {/* Lead Creation Drawer pre-filled with phone number */}
      {createLeadPhone && (
        <LeadDrawer
          open={!!createLeadPhone}
          onClose={() => setCreateLeadPhone(null)}
          onSubmit={data => createLeadMutation.mutate(data)}
          isSubmitting={createLeadMutation.isPending}
          initialData={{ contactNumber: phone10Digits }}
        />
      )}
    </>
  )
})

interface MissedCallCardProps {
  alert: MissedCallAlert
  onCreateLead: (phone: string) => void
}

const MissedCallCard = observer(function MissedCallCard({ alert, onCreateLead }: MissedCallCardProps) {
  const navigate = useNavigate()
  const { event, remainingSeconds, isPaused, id } = alert
  const progressPct = (remainingSeconds / 25) * 100

  const hasEntities =
    event.leads.length > 0 ||
    event.deals.length > 0 ||
    event.sales.length > 0 ||
    event.retentionCustomers.length > 0 ||
    event.contacts.length > 0

  function handleNavigate(path: string) {
    missedCallStore.dismissAlert(id)
    navigate(path)
  }

  function handleCallBack() {
    missedCallStore.dismissAlert(id)
    callStore.showPlaceCall({
      entityType: 'LEAD',
      entityId: event.leads[0]?.id || '',
      name: event.leads[0]?.name || event.contacts[0]?.name || 'Customer',
      phone: event.customerNumber
    })
  }

  return (
    <Paper
      elevation={6}
      onMouseEnter={() => missedCallStore.setPaused(id, true)}
      onMouseLeave={() => missedCallStore.setPaused(id, false)}
      sx={{
        pointerEvents: 'auto',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #fee2e2',
        bgcolor: '#ffffff',
        boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease-in-out'
      }}
    >
      {/* Top Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progressPct}
        sx={{
          height: 4,
          bgcolor: '#fef2f2',
          '& .MuiLinearProgress-bar': {
            bgcolor: isPaused ? '#f59e0b' : '#ef4444',
            transition: 'transform 0.2s linear'
          }
        }}
      />

      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                bgcolor: '#fee2e2',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <MissedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography fontWeight={700} fontSize="14px" color="#0f172a">
                {maskPhoneNumber(event.customerNumber)}
              </Typography>
              <Typography fontSize="11px" color="text.secondary">
                {event.startTime ? `Call time: ${event.startTime}` : 'Recent Call'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={event.callStatus || 'MISSED'}
              size="small"
              sx={{
                bgcolor: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                fontWeight: 700,
                fontSize: '10px',
                height: 20
              }}
            />
            <IconButton
              size="small"
              onClick={() => missedCallStore.dismissAlert(id)}
              sx={{ color: '#94a3b8', p: 0.25, '&:hover': { color: '#0f172a' } }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Content: Matched Entities or Unrecognized Number */}
        <Stack spacing={1} sx={{ mt: 1.5, mb: 1.5 }}>
          {/* Leads */}
          {event.leads.map(lead => (
            <Box
              key={lead.id}
              sx={{
                p: 1,
                borderRadius: '8px',
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LeadIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                <Box>
                  <Typography fontSize="12px" fontWeight={600} color="#1e293b">
                    {lead.name} {lead.companyName ? `(${lead.companyName})` : ''}
                  </Typography>
                  <Typography fontSize="10.5px" color="text.secondary">
                    Lead Status: {lead.status}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                endIcon={<ArrowIcon sx={{ fontSize: '12px !important' }} />}
                onClick={() => handleNavigate(`/leads/${lead.id}`)}
                sx={{ fontSize: '11px', textTransform: 'none', px: 1, py: 0.25, minWidth: 0 }}
              >
                View
              </Button>
            </Box>
          ))}

          {/* Deals */}
          {event.deals.map(deal => (
            <Box
              key={deal.id}
              sx={{
                p: 1,
                borderRadius: '8px',
                bgcolor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DealIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                <Box>
                  <Typography fontSize="12px" fontWeight={600} color="#14532d">
                    {deal.title} {deal.amount ? `— ₹${deal.amount.toLocaleString()}` : ''}
                  </Typography>
                  <Typography fontSize="10.5px" color="#166534">
                    Deal Stage: {deal.stage}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                endIcon={<ArrowIcon sx={{ fontSize: '12px !important' }} />}
                onClick={() => handleNavigate(`/deals/${deal.id}`)}
                sx={{ fontSize: '11px', textTransform: 'none', px: 1, py: 0.25, minWidth: 0, color: '#15803d' }}
              >
                View
              </Button>
            </Box>
          ))}

          {/* Sales */}
          {event.sales.map(sale => (
            <Box
              key={sale.id}
              sx={{
                p: 1,
                borderRadius: '8px',
                bgcolor: '#faf5ff',
                border: '1px solid #e9d5ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SalesIcon sx={{ fontSize: 16, color: '#9333ea' }} />
                <Box>
                  <Typography fontSize="12px" fontWeight={600} color="#581c87">
                    Sales: {sale.dealName}
                  </Typography>
                  <Typography fontSize="10.5px" color="#7e22ce">
                    Status: {sale.status}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                endIcon={<ArrowIcon sx={{ fontSize: '12px !important' }} />}
                onClick={() => handleNavigate(`/sales/${sale.id}`)}
                sx={{ fontSize: '11px', textTransform: 'none', px: 1, py: 0.25, minWidth: 0, color: '#7e22ce' }}
              >
                View
              </Button>
            </Box>
          ))}

          {/* Retention Customers */}
          {event.retentionCustomers.map(ret => (
            <Box
              key={ret.id}
              sx={{
                p: 1,
                borderRadius: '8px',
                bgcolor: '#fff7ed',
                border: '1px solid #ffedd5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompanyIcon sx={{ fontSize: 16, color: '#ea580c' }} />
                <Box>
                  <Typography fontSize="12px" fontWeight={600} color="#7c2d12">
                    Retention: {ret.customerName}
                  </Typography>
                  <Typography fontSize="10.5px" color="#c2410c">
                    Status: {ret.status}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                endIcon={<ArrowIcon sx={{ fontSize: '12px !important' }} />}
                onClick={() => handleNavigate(`/customer-retention/${ret.id}`)}
                sx={{ fontSize: '11px', textTransform: 'none', px: 1, py: 0.25, minWidth: 0, color: '#c2410c' }}
              >
                View
              </Button>
            </Box>
          ))}

          {/* Unrecognized Number Notice */}
          {!hasEntities && (
            <Box
              sx={{
                p: 1.25,
                borderRadius: '8px',
                bgcolor: '#f8fafc',
                border: '1px border-dashed #cbd5e1',
                textAlign: 'center'
              }}
            >
              <Typography fontSize="12px" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Unrecognized Phone Number (Not in CRM)
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Action Buttons: Call Back & + Create Lead */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            fullWidth
            startIcon={<CallIcon sx={{ fontSize: 16 }} />}
            onClick={handleCallBack}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '12px',
              py: 0.75
            }}
          >
            Call Back
          </Button>

          {!hasEntities && (
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<AddLeadIcon sx={{ fontSize: 16 }} />}
              onClick={() => onCreateLead(event.customerNumber)}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '12px',
                py: 0.75,
                borderColor: '#cbd5e1',
                color: '#334155'
              }}
            >
              + Create Lead
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  )
})
